import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { navigate } from "raviger";
import { useTranslation } from "react-i18next";

import CareIcon from "@/CAREUI/icons/CareIcon";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import Page from "@/components/Common/Page";
import { CardListWithHeaderSkeleton } from "@/components/Common/SkeletonLoading";

import query from "@/Utils/request/query";
import BackButton from "@/components/Common/BackButton";
import { PRODUCT_STATUS_COLORS } from "@/types/inventory/product/product";
import productApi from "@/types/inventory/product/productApi";
import { PRODUCT_KNOWLEDGE_TYPE_COLORS } from "@/types/inventory/productKnowledge/productKnowledge";
import { ArrowLeft } from "lucide-react";
import { Link } from "raviger";

interface Props {
  facilityId: string;
  productId: string;
}

export default function ProductView({ facilityId, productId }: Props) {
  const { t } = useTranslation();

  const {
    data: product,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: query(productApi.retrieveProduct, {
      pathParams: {
        facilityId,
        productId,
      },
    }),
  });

  if (isLoading) {
    return <CardListWithHeaderSkeleton count={3} />;
  }

  if (isError || !product) {
    return (
      <Page title={t("error")}>
        <div className="container mx-auto max-w-3xl py-8">
          <Alert variant="destructive">
            <CareIcon icon="l-exclamation-triangle" className="size-4" />
            <AlertTitle>{t("error_loading_product")}</AlertTitle>
            <AlertDescription>{t("product_not_found")}</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate(`/facility/${facilityId}/settings/product`)}
          >
            <CareIcon icon="l-arrow-left" className="mr-2 size-4" />
            {t("back_to_list")}
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <Page title={`Product: ${product.id}`} hideTitleOnPage={true}>
      <div className="container mx-auto max-w-3xl space-y-6">
        <BackButton>
          <ArrowLeft />
          {t("back_to_list")}
        </BackButton>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">
                {t("product")}: {product.id}
              </h1>
              <Badge variant={PRODUCT_STATUS_COLORS[product.status]}>
                {t(product.status)}
              </Badge>
            </div>
            {product.batch?.lot_number && (
              <p className="mt-1 text-sm text-gray-600">
                {t("lot_number")}: {product.batch.lot_number}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `/facility/${facilityId}/settings/product/${product.id}/edit`,
              )
            }
          >
            <CareIcon icon="l-pen" className="mr-2 size-4" />
            {t("edit")}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("product_details")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">{t("status")}</p>
              <p className="font-medium">{t(product.status)}</p>
            </div>
            {product.batch?.lot_number && (
              <div>
                <p className="text-sm text-gray-500">{t("lot_number")}</p>
                <p className="text-gray-700">{product.batch.lot_number}</p>
              </div>
            )}
            {product.expiration_date && (
              <div>
                <p className="text-sm text-gray-500">{t("expiration_date")}</p>
                <p className="text-gray-700">
                  {format(new Date(product.expiration_date), "PPP")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("product_knowledge")}</CardTitle>
            <CardDescription>
              {t("product_knowledge_description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border bg-gray-50/50 p-4 transition-colors hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        PRODUCT_KNOWLEDGE_TYPE_COLORS[
                          product.product_knowledge.product_type
                        ]
                      }
                    >
                      {t(product.product_knowledge.product_type)}
                    </Badge>
                  </div>
                  <h3 className="font-medium">
                    {product.product_knowledge.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {product.product_knowledge.slug}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    navigate(
                      `/facility/${facilityId}/settings/product_knowledge/${product.product_knowledge.slug}`,
                    )
                  }
                >
                  <CareIcon icon="l-eye" className="mr-2 size-4" />
                  {t("view_details")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {product.charge_item_definition && (
          <Card>
            <CardHeader>
              <CardTitle>{t("charge_item_definition")}</CardTitle>
              <CardDescription>
                {t("charge_item_definition_description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border bg-gray-50/50 p-4 transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <h3 className="font-medium">
                      {product.charge_item_definition.title ||
                        product.charge_item_definition.slug}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {product.charge_item_definition.slug}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      basePath="/"
                      href={`/facility/${facilityId}/settings/charge_item_definitions/${product.charge_item_definition.slug}`}
                    >
                      <CareIcon icon="l-eye" className="mr-2 size-4" />
                      {t("view_details")}
                    </Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Page>
  );
}
